import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoIosSearch, IoMdNotificationsOutline } from "react-icons/io";
import { LuPlus } from "react-icons/lu";
import { FaRegEye } from "react-icons/fa";
import { IoChevronDown, IoChevronForward } from "react-icons/io5";
import { Snackbar, Alert } from "@mui/material";
import axiosInstance from "../axiosinstance";
import TodaysReminderPopup from "../Popup/TodaysReminderPopup";
import "./Table.css";
import {
  formatBadgeLabel,
  getStatusToneBadgeClass,
} from "../helperFunctions";

function FamilyGroupReminder({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [families, setFamilies] = useState([]);
  const [familyCount, setFamilyCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedFamilies, setExpandedFamilies] = useState({});
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [invoiceId, setInvoiceId] = useState("");
  const [selectedFamilyId, setSelectedFamilyId] = useState("");

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "-";
    const numericAmount = Number(amount);
    if (Number.isNaN(numericAmount)) return amount;
    return numericAmount.toFixed(2);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleAddReminder = (selectedInvoiceId, familyId = "") => {
    setInvoiceId(selectedInvoiceId);
    setSelectedFamilyId(familyId);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedFamilyId("");
    fetchFamilyGroupReminders();
  };

  const isMissingReminderDate = (value) => {
    if (value == null) return true;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "" || normalized === "null";
    }
    return false;
  };

  const shouldShowManualReminderIcon = (family) => {
    return isMissingReminderDate(family?.next_reminder_date);
  };

  const mapFamilyFromApi = (family) => {
    const mappedInvoices = Array.isArray(family?.invoices) ? family.invoices : [];

    return {
      ...family,
      next_reminder_date: isMissingReminderDate(family?.next_reminder_date)
        ? null
        : family.next_reminder_date,
      invoices: mappedInvoices,
    };
  };

  const handlePlusReminderClick = (event, family) => {
    event.stopPropagation();
    const firstInvoiceId =
      family?.invoices?.find((invoice) => invoice?.invoice_id)?.invoice_id || "";
    handleAddReminder(firstInvoiceId, family?.family_id || "");
  };

  const handleManualReminderIconClick = (event) => {
    event.stopPropagation();
  };

  const handleViewReminder = (familyId, familyName = "") => {
    navigate(`/Reminder-history/${familyId}`, {
      state: {
        historyType: "family_group",
        family_id: familyId,
        family_group_name: familyName,
      },
    });
  };

  const toggleFamily = (familyId) => {
    setExpandedFamilies((prev) => ({
      ...prev,
      [familyId]: !prev[familyId],
    }));
  };

  const fetchFamilyGroupReminders = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosInstance.get("/api/family_group_reminders/v2");
      const fetchedFamilies = (response.data?.families || []).map(mapFamilyFromApi);
      const fetchedFamilyCount = Number(response.data?.family_count) || 0;

      setFamilies(fetchedFamilies);
      setFamilyCount(fetchedFamilyCount);
    } catch (fetchError) {
      console.error(
        "Error fetching family group reminders:",
        fetchError?.message || fetchError
      );
      setFamilies([]);
      setFamilyCount(0);
      setError("Failed to fetch family group reminders");
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to fetch family group reminders");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFamilyGroupReminders();
  }, []);

  const filteredFamilies = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return families;
    }

    return families
      .map((family) => {
        const isFamilyMatch = (family.family_name || "")
          .toLowerCase()
          .includes(normalizedSearch);

        if (isFamilyMatch) {
          return family;
        }

        const filteredInvoices = (family.invoices || []).filter((invoice) => {
          const invoiceClientName = String(invoice.client_name || "").toLowerCase();
          const invoiceNumber = String(invoice.invoice_number || "").toLowerCase();
          return (
            invoiceClientName.includes(normalizedSearch) ||
            invoiceNumber.includes(normalizedSearch)
          );
        });

        if (filteredInvoices.length === 0) {
          return null;
        }

        return {
          ...family,
          invoices: filteredInvoices,
        };
      })
      .filter(Boolean);
  }, [families, search]);

  return (
    <div
      className={`table-container family-group-reminder-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body family-group-reminder-wrap">
        <div className="table-header">
  <div className="table-header-content">
    
    {/* LEFT SIDE */}
    <div className="table-left-side">
      <h2 className="jost-bold">Family Group Reminder Management</h2>
    </div>

    {/* RIGHT SIDE */}
    <div className="table-right-side">
      <div className="search-bar-container">
        <input
          type="text"
          placeholder="Search ..."
          className="search-bar"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <IoIosSearch className="search-bar-icon" />
      </div>
    </div>

  </div>
</div>
        {/* <div className="table-header">
          <div className="table-header-content family-group-header-content">
            <div className="reminder-table-side">
              <h2 className="jost-bold">Family Group Reminder Management</h2>
              <div className="search-right">
                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search ..."
                    className="search-bar"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  <IoIosSearch className="search-bar-icon" />
                </div>
              </div>
            </div>
          </div>
        </div> */}

        <div className="data-list-body family-reminder-data-body">
          <div className="data-list-scroll-body family-reminder-scroll-body">
            {/* <div className="data-list-table"> */}
              <table className="table multilevel-data family-group-table">
                <thead className="sticky-header">
                  <tr className="table-row">
                    <th className="family-group-first-header">
                      <span className="family-group-header-label">
                        Client / Group Name

                      </span>
                    </th>
                    <th className="family-group-first-header">
                      <span className="family-group-header-label">
                        Client Company Name
                      </span>
                    </th>
                    <th>Invoice No</th>
                    <th>Reminder date</th>
                    <th>Invoice Status</th>
                    <th>Due Date</th>
                    <th>Total Amount</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="center-align">
                        Loading family group reminders...
                      </td>
                    </tr>
                  ) : filteredFamilies.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="center-align">
                        {error || "No family group reminders available"}
                      </td>
                    </tr>
                  ) : (
                    filteredFamilies.map((family) => {
                      const isExpanded = Boolean(expandedFamilies[family.family_id]);
                      const showManualReminderIcon =
                        shouldShowManualReminderIcon(family);

                      return (
                        <React.Fragment key={family.family_id}>
                          <tr
                            className="hoverable-row family-parent-row"
                            onClick={() => toggleFamily(family.family_id)}
                          >
                            <td>
                              <div className="family-group-name-cell">
                                <button
                                  type="button"
                                  className="family-expand-btn"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleFamily(family.family_id);
                                  }}
                                >
                                  {isExpanded ? (
                                    <IoChevronDown size={16} />
                                  ) : (
                                    <IoChevronForward size={16} />
                                  )}
                                </button>
                                <span>{family.family_name || "-"}</span>
                              </div>
                            </td>
                            <td className="family-parent-placeholder">-</td>
                            <td className="center-align family-parent-placeholder">-</td>
                            <td className="center-align family-parent-placeholder">
                              {formatDate(family.next_reminder_date)}
                            </td>
                            <td className="center-align family-parent-placeholder">
                              <div
                                className={`value-badge ${getStatusToneBadgeClass(
                                  family.status
                                )}`}
                              >
                                {formatBadgeLabel(family.status, "-")}
                              </div>
                            </td>
                            <td className="center-align family-parent-placeholder">-</td>
                            <td className="center-align amount-right price-left-align">
                              {formatAmount(family.total_outstanding)}
                            </td>
                            <td className="center-align family-action-cell">
                              <div className="action-btn family-action-btn">
                                <span
                                  className="action-icon"
                                  title="Add Reminder"
                                  onClick={(event) =>
                                    handlePlusReminderClick(event, family)
                                  }
                                >
                                  <LuPlus color="gray" />
                                </span>
                                {showManualReminderIcon && (
                                  <span
                                    className="action-icon"
                                    title="Send Manual Reminder"
                                    onClick={handleManualReminderIconClick}
                                  >
                                    <IoMdNotificationsOutline
                                      size={16}
                                      color="gray"
                                    />
                                  </span>
                                )}
                                <span className="action-icon">
                                  <FaRegEye
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (family.family_id) {
                                        handleViewReminder(
                                          family.family_id,
                                          family.family_name
                                        );
                                      }
                                    }}
                                    color="gray"
                                  />
                                </span>
                              </div>
                            </td>
                          </tr>

                          {isExpanded &&
                            (family.invoices || []).map((invoice) => {
                              const normalizedInvoiceStatus = String(
                                invoice.status || ""
                              ).toLowerCase();
                              return (
                              <tr
                                key={invoice.invoice_id}
                                className="hoverable-row family-child-row"
                              >
                                <td className="family-child-primary-cell">{invoice.client_name || "-"}</td>
                                <td>{invoice.client_company_name || "-"}</td>
                                <td className="center-align">
                                  {invoice.invoice_number || "-"}
                                </td>
                                <td className="center-align">
                                  -
                                </td>
                                <td className="center-align">
                                  <div
                                    className={`value-badge ${getStatusToneBadgeClass(
                                      normalizedInvoiceStatus
                                    )}`}
                                  >
                                    {formatBadgeLabel(invoice.status, "-")}
                                  </div>
                                </td>
                                <td className="center-align">
                                  {formatDate(invoice.due_date)}
                                </td>
                                <td className="center-align amount-right price-left-align">
                                  {formatAmount(invoice.total_amount)}
                                </td>
                                <td className="center-align family-action-cell family-child-action-empty">
                                  &nbsp;
                                </td>
                              </tr>
                              );
                            })}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            {/* </div> */}
          </div>
        </div>
      </div>

      <TodaysReminderPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        invoice_id={invoiceId}
        family_id_for_count={selectedFamilyId}
        family_group_count={familyCount}
        use_family_count_api={true}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default FamilyGroupReminder;
